/**
 * promoPricing.ts — display-side mirror of backend promoService.
 * Decides which discount a price card shows: campaign (e.g. BF2026) or PPP.
 * Checkout re-decides server-side; this only keeps the UI in step with it.
 *
 * Placement: frontend/src/utils/promoPricing.ts
 */

export interface PromoCampaign {
  code: string;
  label: string | null;
  bannerText: string | null;
  selfHostedOffer: string | null;
  discountPct: number;
  billingCycles: Array<'monthly' | 'annual'>;
  durationMonths: number | null;
  eligibility: 'all' | 'first_paid';
  tierCodes: string[];
  startsAt: string | null;
  endsAt: string | null;
  claimExpiresAt?: string | null;
}

export type BillingCycle = 'monthly' | 'annual';

export interface DisplayDiscount {
  kind: 'campaign' | 'parity' | null;
  pct: number;          // % off shown on the card (0 = none)
  factor: number;       // multiply list price by this
  badge: string | null; // pill under the price
  hint: string | null;  // campaign exists but for the other cycle
}

const flag = (cc: string | null | undefined): string =>
  cc && cc.length === 2
    ? cc.toUpperCase().replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)))
    : '';

/** Same rule as backend: annual = full %, monthly pro-rated by duration. */
export function yearOnePct(c: PromoCampaign, cycle: BillingCycle): number {
  if (cycle === 'annual') return c.discountPct;
  if (!c.durationMonths) return c.discountPct;
  return (c.discountPct * Math.min(c.durationMonths, 12)) / 12;
}

/** Same rule as backend: campaign must at least double PPP in year one. */
export function campaignBeatsParity(campaignYearOne: number, parityPct: number | null | undefined): boolean {
  return campaignYearOne > 0 && campaignYearOne >= 2 * (parityPct || 0);
}

export function campaignAppliesTo(c: PromoCampaign | null | undefined, tierCode: string | null | undefined, cycle: BillingCycle): boolean {
  if (!c) return false;
  if (!c.billingCycles.includes(cycle)) return false;
  if (tierCode && c.tierCodes.length && !c.tierCodes.includes(tierCode)) return false;
  return true;
}

function durationText(c: PromoCampaign, cycle: BillingCycle): string {
  if (cycle === 'annual') return 'first year';
  if (!c.durationMonths) return '';
  return c.durationMonths === 1 ? 'first month' : `first ${c.durationMonths} months`;
}

export function getDisplayDiscount(opts: {
  billingCycle: BillingCycle;
  tierCode?: string | null;
  parityPct?: number | null;
  parityCountry?: string | null;
  campaign?: PromoCampaign | null;
}): DisplayDiscount {
  const { billingCycle, tierCode, parityPct, parityCountry, campaign } = opts;
  const p = parityPct && parityPct > 0 ? parityPct : 0;

  if (campaign && campaignAppliesTo(campaign, tierCode, billingCycle) &&
      campaignBeatsParity(yearOnePct(campaign, billingCycle), p)) {
    const d = durationText(campaign, billingCycle);
    return {
      kind: 'campaign',
      pct: campaign.discountPct,
      factor: (100 - campaign.discountPct) / 100,
      badge: campaign.label || `${campaign.discountPct}% off${d ? ` your ${d}` : ''}`,
      hint: null,
    };
  }

  // Campaign for the other cycle (e.g. annual-only) that this user would get there.
  let hint: string | null = null;
  if (campaign) {
    const other: BillingCycle = billingCycle === 'annual' ? 'monthly' : 'annual';
    if (campaignAppliesTo(campaign, tierCode, other) &&
        campaignBeatsParity(yearOnePct(campaign, other), p)) {
      hint = `${campaign.label || `${campaign.discountPct}% off`} on ${other} plans`;
    }
  }

  if (p > 0) {
    return {
      kind: 'parity',
      pct: p,
      factor: (100 - p) / 100,
      badge: `${flag(parityCountry)} ${parityCountry || 'Regional'} pricing · −${p}%`.trim(),
      hint,
    };
  }
  return { kind: null, pct: 0, factor: 1, badge: null, hint };
}
